import { useMemo, useState } from 'react';
import {
  CRAPS_BETS,
  CRAPS_PRESS_MODES,
  canPlaceCrapsBet,
  canPullCrapsBet,
  crapsPressPlan,
  maxCrapsOdds,
  nextCrapsPressMode,
  type CrapsBetId,
  type CrapsPressMode,
  type CrapsRules,
} from '@tv-casino/shared';
import type { PublicCrapsBet } from '@tv-casino/shared';
import { CrapsTable, type CrapsSpotMark } from './CrapsTable.js';
import { FloatAction, useOffscreenWatch } from './FloatAction.js';
import type { CrapsStackMap } from '@tv-casino/viewmodels/components/crapsFelt.js';
import { chipTray } from '@tv-casino/viewmodels/components/chipStack.js';
import styles from './CrapsBet.module.css';

/** One of this seat's place or hardway bets that the last roll paid. */
export interface CrapsHit {
  betId: CrapsBetId;
  label: string;
  /** The stake the roll paid on. */
  stake: number;
  /** Chips it returned, that stake included. */
  payout: number;
  /** What is on the number right now off the back of that hit. */
  rides: number;
}

interface CrapsBetProps {
  rules: CrapsRules;
  /** The point, or null on the come-out: it decides what is still open. */
  point: number | null;
  /** Chips free to stake, outside anything already on the felt. */
  chips: number;
  /** Every stake on the layout, this seat's included. */
  stacks: CrapsStackMap;
  /** What the last roll did, spot by spot. */
  marks: Partial<Record<CrapsBetId, CrapsSpotMark>>;
  /** This seat's bets, one per spot — the working toggles read from these. */
  bets: PublicCrapsBet[];
  /** Place and hardway bets the last roll just paid, while the answer is live. */
  hits: CrapsHit[];
  /** Total this seat has on the felt. */
  staked: number;
  /** True once the seat locked in for the come-out window. */
  ready: boolean;
  /** False between hands: the felt still shows, but takes no new chips. */
  canDrop: boolean;
  /** True only in the come-out window, where Ready means something. */
  showReady: boolean;
  /** True once the dice are live, whoever is holding them. */
  rolling: boolean;
  /** True when the dice are this seat's to send out. */
  canRoll: boolean;
  /** Who is holding them, for the beat where this seat is only watching. */
  shooterName: string | null;
  busy: boolean;
  onPlace: (betId: CrapsBetId, amount: number) => void;
  /** Take one bet back, or everything pullable when no bet is named. */
  onClear: (betId?: CrapsBetId) => void;
  onWorking: (betId: CrapsBetId, working: boolean) => void;
  /** Say what a hit on this bet does with the chips. */
  onPress: (betId: CrapsBetId, press: CrapsPressMode) => void;
  /** Answer the hit that just paid, before the next roll goes out. */
  onPressNow: (betId: CrapsBetId, press: CrapsPressMode) => void;
  onReady: () => void;
  onRoll: () => void;
}

const chipTone = (amount: number): string | undefined =>
  amount >= 500
    ? styles.tone500
    : amount >= 100
      ? styles.tone100
      : amount >= 25
        ? styles.tone25
        : styles.tone5;

const cx = (...parts: (string | false | null | undefined)[]) => parts.filter(Boolean).join(' ');

/** How far a pointer may wander and still count as a tap, not a drag. */
const TAP_SLOP_PX = 8;

/** What each press call sounds like when the box calls it back. */
const PRESS_LABEL: Record<CrapsPressMode, string> = {
  down: 'take it down',
  same: 'same bet',
  press: 'press it',
};

/**
 * The phone's craps table: the real layout, half a table wide, with everyone's
 * chips sitting on the spots they were bet on. Pick a chip out of the rail and
 * tap a spot to drop it — every tap is a bet, exactly like sliding a chip
 * across the felt — or drag one over if you would rather aim.
 *
 * Chips stay where you put them. They ride from roll to roll and across hands
 * until you tap your own stack to take it back; the one thing that will not
 * come down is a pass line bet with the puck ON.
 */
export function CrapsBet({
  rules,
  point,
  chips,
  stacks,
  marks,
  bets,
  hits,
  staked,
  ready,
  canDrop,
  showReady,
  rolling,
  canRoll,
  shooterName,
  busy,
  onPlace,
  onClear,
  onWorking,
  onPress,
  onPressNow,
  onReady,
  onRoll,
}: CrapsBetProps) {
  const tray = useMemo(() => chipTray(rules.minBet), [rules.minBet]);
  const [denom, setDenom] = useState(tray[0]!);
  const [ghost, setGhost] = useState<{ x: number; y: number; over: CrapsBetId | null } | null>(null);
  const rollWatch = useOffscreenWatch<HTMLButtonElement>(canRoll);

  const on = (id: CrapsBetId) => bets.find((b) => b.id === id) ?? null;
  const lineFor = (id: CrapsBetId) => on(id === 'passOdds' ? 'pass' : 'dontPass')?.amount ?? 0;

  /** The most this spot will take right now: the sign, or the odds cap. */
  const ceiling = (id: CrapsBetId): number => {
    const already = on(id)?.amount ?? 0;
    if (id === 'passOdds' || id === 'dontOdds') {
      return Math.max(0, maxCrapsOdds(id, lineFor(id), point, rules) - already);
    }
    return Math.max(0, rules.maxBet - already);
  };

  const takes = (id: CrapsBetId): boolean =>
    canDrop &&
    denom <= chips &&
    canPlaceCrapsBet(id, point, rules) &&
    ceiling(id) >= denom;

  const pullable = (id: CrapsBetId): boolean => !busy && canPullCrapsBet(id, point);

  const drop = (id: CrapsBetId) => {
    if (!takes(id)) return;
    onPlace(id, denom);
  };

  /** The spot under the pointer, read off the felt itself rather than refs. */
  const spotAt = (x: number, y: number): CrapsBetId | null => {
    // jsdom does no hit-testing, so under test a drag simply lands nowhere.
    if (typeof document.elementFromPoint !== 'function') return null;
    const el = document.elementFromPoint(x, y)?.closest('[data-craps-spot]');
    return (el?.getAttribute('data-craps-spot') as CrapsBetId | null) ?? null;
  };

  const beginDrag = (chip: number, e: React.PointerEvent<HTMLButtonElement>) => {
    setDenom(chip);
    if (chip > chips) return;
    e.preventDefault();
    const start = { x: e.clientX, y: e.clientY };
    setGhost({ x: e.clientX, y: e.clientY, over: null });

    const teardown = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', end);
      window.removeEventListener('pointercancel', teardown);
      setGhost(null);
    };
    const move = (ev: PointerEvent) =>
      setGhost({ x: ev.clientX, y: ev.clientY, over: spotAt(ev.clientX, ev.clientY) });
    const end = (ev: PointerEvent) => {
      const target = spotAt(ev.clientX, ev.clientY);
      teardown();
      // A tap on the rail only arms the chip; a drag has to land on a spot.
      if (Math.hypot(ev.clientX - start.x, ev.clientY - start.y) < TAP_SLOP_PX) return;
      if (target) drop(target);
    };

    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', end);
    window.addEventListener('pointercancel', teardown);
  };

  /** Place and hardway bets sleep through a come-out unless they are called. */
  const parkable = bets.filter((b) => CRAPS_BETS[b.id].offOnComeOut);
  const canSweep = bets.some((b) => canPullCrapsBet(b.id, point));

  /**
   * One button, one slot, all hand long. The come-out asks for Ready, live
   * dice ask the shooter for a roll, and every other beat names whoever the
   * table is waiting on — so the control never pops in or out from under the
   * felt while someone is aiming a chip at it.
   *
   * A seat with nothing down still gets to answer: an empty spread is a valid
   * answer to a come-out, and holding the window open on it only stalls the
   * table until the bet clock runs out. The dice only go out once somebody
   * has the line backed, and the button says so while nobody does.
   */
  const lineUp = (stacks.pass?.length ?? 0) > 0 || (stacks.dontPass?.length ?? 0) > 0;
  const sendOff = lineUp ? 'send them out' : 'the dice need a line bet';
  const primary: { label: string; on: boolean; act: (() => void) | null } = canRoll
    ? { label: 'Roll the dice', on: true, act: onRoll }
    : rolling
      ? { label: shooterName ? `${shooterName} is shooting` : 'The dice are out', on: false, act: null }
      : !showReady
        ? { label: 'Between hands', on: false, act: null }
        : ready
          ? { label: lineUp ? 'Locked in' : `Locked in — ${sendOff}`, on: true, act: null }
          : {
              label: staked === 0 ? `Nothing down — ${sendOff}` : `Ready — ${sendOff}`,
              on: false,
              act: onReady,
            };

  return (
    <section className={styles.wrap}>
      <p className={styles.heading}>
        {point === null ? 'Craps — come out' : `Craps — point ${point}`}
      </p>

      <CrapsTable
        rules={rules}
        point={point}
        stacks={stacks}
        marks={marks}
        armed={ghost?.over ?? null}
        canDrop={takes}
        onDrop={drop}
        onPull={(id) => onClear(id)}
        canPull={pullable}
      />

      <div className={styles.rail} role="group" aria-label="Chip rail">
        {tray.map((chip) => (
          <button
            key={chip}
            type="button"
            className={cx(
              styles.railChip,
              chipTone(chip),
              denom === chip && styles.railChipOn,
              chip > chips && styles.railChipOff,
            )}
            aria-pressed={denom === chip}
            aria-label={`${chip} chip`}
            onPointerDown={(e) => beginDrag(chip, e)}
          >
            {chip}
          </button>
        ))}
        <span className={styles.railHint}>
          {canDrop ? 'Tap a spot to drop' : 'Between hands'}
        </span>
      </div>

      {hits.length > 0 && (
        <ul className={styles.hits} aria-label="Bets that just hit">
          {hits.map((h) => {
            const current = on(h.betId)?.amount ?? 0;
            // Chips added to the number by hand since the hit stay put: the
            // call only restates the part of the stack the hit left there.
            const extra = Math.max(0, current - h.rides);
            return (
              <li key={h.betId} className={styles.hitRow}>
                <span className={styles.hitLine}>
                  {h.label} paid {h.payout - h.stake} — press it?
                </span>
                <span className={styles.hitCalls}>
                  {CRAPS_PRESS_MODES.map((mode) => {
                    const target = crapsPressPlan(mode, h.stake, h.payout, rules.maxBet).rides + extra;
                    const delta = target - current;
                    return (
                      <button
                        key={mode}
                        type="button"
                        className={target === current ? styles.hitCallOn : styles.hitCall}
                        disabled={busy || delta > chips || target > rules.maxBet}
                        aria-pressed={target === current}
                        aria-label={`${h.label} · ${PRESS_LABEL[mode]} · ${target} up`}
                        onClick={() => onPressNow(h.betId, mode)}
                      >
                        {PRESS_LABEL[mode]} <b>{target}</b>
                      </button>
                    );
                  })}
                </span>
              </li>
            );
          })}
        </ul>
      )}

      {parkable.length > 0 && (
        <ul className={styles.parked} aria-label="Place and hardway bets">
          {parkable.map((b) => (
            <li key={b.id} className={styles.parkedRow}>
              <button
                type="button"
                className={b.working ? styles.workingOn : styles.working}
                disabled={busy}
                aria-pressed={b.working}
                onClick={() => onWorking(b.id, !b.working)}
              >
                {CRAPS_BETS[b.id].label} · {b.working ? 'working' : 'off on come-out'}
              </button>
              <button
                type="button"
                className={b.press === 'press' ? styles.pressOn : styles.press}
                disabled={busy}
                aria-label={`${CRAPS_BETS[b.id].label} · ${PRESS_LABEL[b.press]} when it hits`}
                onClick={() => onPress(b.id, nextCrapsPressMode(b.press))}
              >
                {PRESS_LABEL[b.press]}
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className={styles.actions}>
        <button
          type="button"
          className={styles.sweep}
          disabled={busy || !canSweep}
          onClick={() => onClear()}
        >
          Take back all ({staked})
        </button>
        <button
          ref={rollWatch.ref}
          type="button"
          className={primary.on ? styles.readyOn : styles.ready}
          disabled={busy || primary.act === null}
          onClick={() => primary.act?.()}
        >
          {primary.label}
        </button>
      </div>

      <FloatAction
        show={canRoll && rollWatch.offscreen}
        buttons={[{ label: 'Roll the dice', disabled: busy, onClick: onRoll }]}
      />

      <p className={styles.hint}>
        {point === null
          ? 'Back the line, then anything else. Place and hardway bets sleep through the come-out unless you call them working.'
          : 'The line is closed until the next come-out. Odds, place bets, the field and the props are all live. Tap your own chips to take them back.'}
        {parkable.length > 0
          ? ' A place or hardway bet that hits stays on its number — tap the call to press the win back up, or take it down.'
          : ''}
      </p>

      {ghost && (
        <span className={cx(styles.ghost, chipTone(denom))} style={{ left: ghost.x, top: ghost.y }}>
          {denom}
        </span>
      )}
    </section>
  );
}
